import React, { useEffect, useState } from 'react';
import { FaTimes, FaSyncAlt } from 'react-icons/fa';
import { API_BASE_URL, getHeaders } from '../../utils/api';
import { ApiEndpoints } from '../../utils/ApiEndpoints';
import { normalizeDomain, readSitePolicyError } from '../../utils/siteDomainConfiguration';

const MAX_FRESHNESS_SECONDS = 60;
const FLAGS = ['consumerExperience', 'heat_map', 'customer_analytics'];
const READINESS = ['disabled', 'ready', 'partial', 'blocked', 'pending', 'stale'];
const ADMISSION = ['available', 'at_capacity', 'over_limit', 'unknown', 'pending', 'stale'];
const REASONS = {
  organization_inactive: 'Organization is inactive.',
  origin_not_enabled: 'Domain is not enabled in the origin registry.',
  origin_ownership_mismatch: 'Origin belongs to another site.',
  site_deleting: 'Site deletion is pending.',
  site_unavailable: 'Site is unavailable.',
  site_inactive: 'Site is inactive.',
  domain_not_registered: 'Domain is not registered on this site.',
  domain_disabled: 'Domain capabilities are disabled.',
  origin_limit_unavailable: 'Extension origin limit is unavailable.',
  origin_limit_exceeded: 'Domain is outside the admitted extension origin capacity.',
  extension_player_unavailable: 'Extension player entitlement is unavailable.',
  heat_map_unavailable: 'Basic heatmap entitlement is unavailable.',
  customer_analytics_unavailable: 'Advanced customer analytics entitlement is unavailable.',
  capture_storage_unavailable: 'Capture storage is unavailable.',
  capture_disabled: 'Site capture settings are disabled.',
  advanced_capture_disabled: 'Advanced site capture settings are disabled.',
  advanced_mappings_missing: 'Advanced page/action mappings are missing. Capability flags do not guarantee usable advanced capture.',
  extension_policy_storage_unavailable: 'Policy storage is unavailable; effective grants cannot be confirmed.',
  extension_policy_pending: 'Domain policy synchronization is pending.',
  policy_changed: 'Policy changed during evaluation; refresh required.'
};

const READINESS_BADGES = {
  ready: 'bg-green-100 text-green-800 border-green-200',
  blocked: 'bg-red-100 text-red-800 border-red-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  partial: 'bg-blue-100 text-blue-800 border-blue-200',
  stale: 'bg-gray-100 text-gray-700 border-gray-200',
  disabled: 'bg-gray-100 text-gray-500 border-gray-200'
};

const validFlags = value => value && FLAGS.every(flag => typeof value[flag] === 'boolean');
const count = value => Number.isInteger(value) && value >= 0;
const validQuota = quota => {
  if (!quota || !ADMISSION.includes(quota.admission)) return false;
  if (['pending', 'stale'].includes(quota.admission)) {
    return quota.consumed === null && quota.allowed === null && quota.remaining === null && quota.isUnlimited === null;
  }
  if (quota.admission === 'unknown') {
    return (quota.consumed === null || count(quota.consumed)) &&
      quota.allowed === null && quota.remaining === null && quota.isUnlimited === null;
  }
  if (!count(quota.consumed)) return false;
  if (quota.isUnlimited === true) {
    return quota.admission === 'available' && quota.allowed === null && quota.remaining === null;
  }
  return quota.isUnlimited === false && count(quota.allowed) && count(quota.remaining);
};
const validOperation = operation => operation === null || (
  typeof operation?.operationId === 'string' && operation.operationId.length > 0 &&
  typeof operation.siteCollectionId === 'string' && operation.siteCollectionId.length > 0 &&
  ['pending', 'retryable', 'completed'].includes(operation.status) && typeof operation.canRetry === 'boolean'
);
const validState = (data, organizationId, siteCollectionId) =>
  data?.organizationId === organizationId && data.siteCollectionId === siteCollectionId &&
  typeof data.policyVersion === 'string' && data.policyVersion.length > 0 &&
  Number.isInteger(data.expiresIn) && data.expiresIn >= 0 && validOperation(data.operation) && validQuota(data.originQuota) &&
  Array.isArray(data.domains) && data.domains.every(domain =>
    typeof domain?.domain === 'string' && domain.domain.length > 0 &&
    validFlags(domain.requested) && validFlags(domain.effective) &&
    (domain.originSlotAllocated === null || typeof domain.originSlotAllocated === 'boolean') &&
    READINESS.includes(domain.readiness) && Array.isArray(domain.reasons) &&
    domain.reasons.every(reason => typeof reason === 'string')) &&
  new Set(data.domains.map(domain => normalizeDomain(domain.domain))).size === data.domains.length;

const renderFlags = flags => FLAGS.map(flag => `${flag}: ${flags[flag] ? 'On' : 'Off'}`).join('; ');

export default function SiteDomainConfigurationState({
  organizationId, siteCollectionId, token, refreshKey = 0, isSaving = false,
  draftDomains = [], onOperation, onDismiss, onNavigate
}) {
  const [generation, setGeneration] = useState(0);
  const [snapshot, setSnapshot] = useState(null);
  const scope = `${organizationId}:${siteCollectionId}`;
  const isTargetMatch = snapshot?.scope === scope && snapshot?.token === token && snapshot?.refreshKey === refreshKey;
  const isPendingGeneration = isTargetMatch && snapshot.generation !== generation;
  const isLoading = !isTargetMatch || snapshot.loading || isPendingGeneration;
  const current = isTargetMatch ? { ...snapshot, loading: isLoading } : null;

  useEffect(() => {
    if (!organizationId || !siteCollectionId || isSaving) return undefined;
    const controller = new AbortController();
    let expiryTimer;
    const identity = { scope, token, refreshKey, generation };
    const startedAt = Date.now();
    setSnapshot(previous => {
      const isSameTarget = previous?.scope === scope && previous?.token === token && previous?.refreshKey === refreshKey;
      return {
        ...identity,
        loading: true,
        data: isSameTarget ? previous.data : null,
        expiresAt: isSameTarget ? previous.expiresAt : null,
        expired: isSameTarget ? previous.expired : false,
        error: null
      };
    });
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}${ApiEndpoints.domainConfigurationState(organizationId, siteCollectionId)}`, {
          headers: getHeaders(token), signal: controller.signal, cache: 'no-store'
        });
        if (!response.ok || response.status === 202) throw await readSitePolicyError(response);
        const data = await response.json();
        if (!validState(data, organizationId, siteCollectionId)) {
          throw new Error('The server returned invalid or mismatched domain configuration state. Effective capabilities are unknown.');
        }
        if (controller.signal.aborted) return;
        const expiresAt = startedAt + Math.min(data.expiresIn, MAX_FRESHNESS_SECONDS) * 1000;
        setSnapshot({ ...identity, data, expiresAt, loading: false, error: null });
        if (expiresAt > Date.now()) {
          expiryTimer = setTimeout(() => {
            if (!controller.signal.aborted) setSnapshot(previous => ({ ...previous, expired: true }));
          }, expiresAt - Date.now());
        }
        if (data.operation && data.operation.status !== 'completed') onOperation?.(data.operation);
      } catch (error) {
        if (!controller.signal.aborted) setSnapshot({
          ...identity, loading: false, data: null,
          error: error.message || 'Domain configuration state is unavailable. Effective capabilities are unknown.'
        });
      }
    };
    load();
    return () => { controller.abort(); clearTimeout(expiryTimer); };
  }, [organizationId, siteCollectionId, token, refreshKey, generation, isSaving, scope, onOperation]);

  useEffect(() => {
    const refresh = () => setGeneration(value => value + 1);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const data = !isSaving ? current?.data : null;
  const fresh = data && (!data.operation || data.operation.status === 'completed') &&
    !['pending', 'stale'].includes(data.originQuota.admission) &&
    !current?.expired && current?.expiresAt > Date.now();
  const unsavedDomains = data ? draftDomains.map(normalizeDomain).filter(domain => !domain.includes('*') &&
    !data.domains.some(saved => normalizeDomain(saved.domain) === domain)) : [];

  return (
    <section aria-label="Saved domain configuration state" className="mt-6 rounded-lg border border-border bg-card p-5 text-card-foreground shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-base font-semibold tracking-tight text-foreground">Saved requested and effective domain capabilities</h4>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss saved state"
            className="text-xs text-muted-foreground hover:text-foreground p-1 rounded hover:bg-muted transition-colors cursor-pointer"
            title="Dismiss state view"
          >
            <FaTimes size={14} />
          </button>
        )}
      </div>
      {!siteCollectionId ? <p className="text-xs text-muted-foreground italic">Not yet saved. This draft has no server-effective state.</p> : (
        <>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={isSaving || current?.loading}
              onClick={() => setGeneration(value => value + 1)}
              className="inline-flex items-center gap-1.5 justify-center rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <FaSyncAlt className={`text-muted-foreground ${current?.loading ? 'animate-spin' : ''}`} size={11} />
              {current?.loading ? 'Refreshing...' : 'Refresh effective state'}
            </button>
            {current?.loading && Boolean(data) && (
              <span className="text-xs text-muted-foreground italic">Refreshing effective capabilities...</span>
            )}
          </div>
          {isSaving ? <p role="status" className="text-xs text-muted-foreground italic">Save in progress. Effective state is unconfirmed.</p> :
            current?.error ? <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs font-medium text-destructive">Domain state unavailable: {current.error}</p> :
              !data && (!current || current?.loading) ? <p role="status" className="text-xs text-muted-foreground italic">Loading saved domain state. Effective capabilities are unknown.</p> : null}
          {data && (
            <div className={`space-y-4 transition-opacity duration-150 ${current?.loading ? 'opacity-70' : 'opacity-100'}`}>
              <span style={{ display: 'none' }}>{data.policyVersion}</span>
              {!fresh && <p role="status" className="rounded-md border border-amber-200 bg-amber-50/80 p-2.5 text-xs text-amber-900">State expired or changed. Effective capabilities are unconfirmed; refresh before relying on them.</p>}
              <section aria-label="Authoritative origin quota" className="rounded-md border border-border bg-muted/20 p-3 space-y-2">
                <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Organization origin quota</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <p className="rounded border border-border bg-background p-2.5 font-medium text-foreground">Origin admission: {data.originQuota.admission}{!fresh ? ' (unconfirmed snapshot)' : ''}.</p>
                  <p className="rounded border border-border bg-background p-2.5 font-medium text-foreground">Consumed: {fresh ? data.originQuota.consumed ?? 'Unknown' : 'Unknown'}.</p>
                  <p className="rounded border border-border bg-background p-2.5 font-medium text-foreground">Allowed: {fresh ? data.originQuota.isUnlimited === true ? 'Unlimited' : data.originQuota.allowed ?? 'Unknown' : 'Unknown'}.</p>
                  <p className="rounded border border-border bg-background p-2.5 font-medium text-foreground">Remaining: {fresh ? data.originQuota.isUnlimited === true ? 'Not capped (unlimited)' : data.originQuota.remaining ?? 'Unknown' : 'Unknown'}.</p>
                </div>
                <p className="text-xs text-muted-foreground italic">Rendering and basic capture share one slot per organization domain. Allocation does not itself prove effective access.</p>
              </section>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-left text-[11px] border-collapse table-auto">
                  <thead className="border-b border-border bg-muted/60 text-muted-foreground font-medium">
                    <tr>
                      <th className="px-2.5 py-2 font-medium">Domain</th>
                      <th className="px-2.5 py-2 font-medium">Saved requested (snapshot)</th>
                      <th className="px-2.5 py-2 font-medium">Effective</th>
                      <th className="px-2.5 py-2 font-medium">Readiness</th>
                      <th className="px-2.5 py-2 font-medium">Reasons</th>
                      <th className="px-2.5 py-2 font-medium">Origin slot</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.domains.map(domain => (
                      <tr key={domain.domain} className="hover:bg-muted/30 transition-colors">
                        <th scope="row" className="px-2.5 py-2 font-mono font-medium text-foreground align-top">{domain.domain}</th>
                        <td className="px-2.5 py-2 text-muted-foreground font-mono text-[11px] align-top">{renderFlags(domain.requested)}</td>
                        <td className="px-2.5 py-2 text-foreground font-mono text-[11px] align-top">{fresh && !['pending', 'stale'].includes(domain.readiness)
                          ? renderFlags(domain.effective) : 'Unconfirmed'}</td>
                        <td className="px-2.5 py-2 align-top">
                          <span
                            title={domain.readiness === 'partial'
                              ? 'Entitlements granted & slot allocated. Awaiting action registration in Heatmap Settings to activate funnels.'
                              : undefined}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${READINESS_BADGES[domain.readiness] || 'bg-muted text-muted-foreground border-border'}`}
                          >
                            {domain.readiness}
                          </span>
                          {!fresh ? ' (expired snapshot)' : ''}
                        </td>
                        <td className="px-2.5 py-2 text-xs text-muted-foreground align-top space-y-1">
                          {domain.reasons.length ? domain.reasons.map(reason => (
                            <div key={reason} className="leading-snug space-y-1">
                              <p>
                                {REASONS[reason] || 'Server reported a capability restriction.'} <code className="font-mono text-[11px] bg-muted px-1 py-0.5 rounded border border-border">{reason}</code>
                              </p>
                              {reason === 'advanced_mappings_missing' && onNavigate && (
                                <button
                                  type="button"
                                  onClick={() => onNavigate('heatmap-settings', { siteCollectionId, organizationId })}
                                  className="inline-flex items-center gap-1 font-medium text-blue-600 underline hover:text-blue-800 text-[11px] cursor-pointer"
                                >
                                  Configure in Heatmap Settings &rarr;
                                </button>
                              )}
                            </div>
                          )) : 'No blocking reasons reported.'}
                        </td>
                        <td className="px-2.5 py-2 text-xs text-foreground align-top">
                          {!fresh || domain.originSlotAllocated === null ? 'Unknown' :
                            domain.originSlotAllocated ? 'Allocated' : 'Not allocated'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {unsavedDomains.map(domain => <p key={domain} className="rounded border border-amber-200 bg-amber-50/70 p-2 text-xs text-amber-900 font-mono">{domain}: Not yet saved; effective capabilities are unknown.</p>)}
              {data.domains.length === 0 && <p className="text-xs text-muted-foreground italic">No saved concrete-domain capability entries.</p>}
            </div>
          )}
        </>
      )}
    </section>
  );
}
